/**
 * Builds pre-generated footer strings at SSR time. Footer always receives these and never uses react-intl.
 *
 * Message ids and English fallbacks live in footer.messages.ts (the formatjs
 * extraction source for all footer.* ids) — keep new footer strings there.
 */
import type { Messages } from '../../types';
import { footerMessages } from './footer.messages';

export interface FooterStaticStrings {
  aiTranslationDisclaimer: string;
  copyrightBefore: string;
  copyrightAfter: string;
  businessIdLabel: string;
  brandDescription: string;
  colFindJobs: string;
  colJobSeekers: string;
  colCompany: string;
  colEmployersAndDevelopers: string;
  openJobsInFinland: string;
  jobsInHelsinki: string;
  jobsInTampere: string;
  jobsInEspoo: string;
  jobsInTurku: string;
  jobsInOulu: string;
  remoteJobs: string;
  allLocations: string;
  browseAllPages: string;
  salariesByRole: string;
  bestEmployers: string;
  collectiveAgreementsLink: string;
  guidesAndArticles: string;
  signIn: string;
  aboutJobcrawls: string;
  partnerships: string;
  editorialStandards: string;
  howCrawlerWorks: string;
  forEmployersLink: string;
  freeCareersPageLink: string;
  forDevelopers: string;
  connectorPrivacy: string;
  contact: string;
  privacy: string;
  terms: string;
  cookieSettings: string;
  accessibilityStatementLink: string;
  sitemap: string;
}

/** Parse "© 2026 {company}" into before/after. */
function parseCopyright(template: string): { before: string; after: string } {
  const idx = template.indexOf('{company}');
  if (idx === -1) return { before: template, after: '' };
  return {
    before: template.slice(0, idx),
    after: template.slice(idx + '{company}'.length),
  };
}

export function buildFooterStaticStrings(messages: Messages): FooterStaticStrings {
  const rawCopyright =
    messages[footerMessages.copyright.id] ?? footerMessages.copyright.defaultMessage;
  const { before: copyrightBefore, after: copyrightAfter } = parseCopyright(rawCopyright);

  const get = (key: Exclude<keyof FooterStaticStrings, 'copyrightBefore' | 'copyrightAfter'>): string =>
    messages[footerMessages[key].id] ?? footerMessages[key].defaultMessage;

  return {
    aiTranslationDisclaimer: get('aiTranslationDisclaimer'),
    copyrightBefore,
    copyrightAfter,
    businessIdLabel: get('businessIdLabel'),
    brandDescription: get('brandDescription'),
    colFindJobs: get('colFindJobs'),
    colJobSeekers: get('colJobSeekers'),
    colCompany: get('colCompany'),
    colEmployersAndDevelopers: get('colEmployersAndDevelopers'),
    openJobsInFinland: get('openJobsInFinland'),
    jobsInHelsinki: get('jobsInHelsinki'),
    jobsInTampere: get('jobsInTampere'),
    jobsInEspoo: get('jobsInEspoo'),
    jobsInTurku: get('jobsInTurku'),
    jobsInOulu: get('jobsInOulu'),
    remoteJobs: get('remoteJobs'),
    allLocations: get('allLocations'),
    browseAllPages: get('browseAllPages'),
    salariesByRole: get('salariesByRole'),
    bestEmployers: get('bestEmployers'),
    collectiveAgreementsLink: get('collectiveAgreementsLink'),
    guidesAndArticles: get('guidesAndArticles'),
    signIn: get('signIn'),
    aboutJobcrawls: get('aboutJobcrawls'),
    partnerships: get('partnerships'),
    editorialStandards: get('editorialStandards'),
    howCrawlerWorks: get('howCrawlerWorks'),
    forEmployersLink: get('forEmployersLink'),
    freeCareersPageLink: get('freeCareersPageLink'),
    forDevelopers: get('forDevelopers'),
    connectorPrivacy: get('connectorPrivacy'),
    contact: get('contact'),
    privacy: get('privacy'),
    terms: get('terms'),
    cookieSettings: get('cookieSettings'),
    accessibilityStatementLink: get('accessibilityStatementLink'),
    sitemap: get('sitemap'),
  };
}
